<template>
    <v-card class="white rounded-lg px-4 py-4 mb-5 offensive-rating-card" outlined>
        <v-card-title class="secondary-font py-0 section-title title-row">
            <span>Off. / Def. Rating Diagram</span>
            <info-tip bottom max-width="520" content-class="ts-tooltip-surface diagram-tooltip-surface diagram-tooltip-surface--off">
                <v-icon
                    small
                    class="ml-2 info-icon"
                    color="orange"
                >
                    mdi-information-outline
                </v-icon>
                <template #content>
                    <div class="tooltip-content">
                        <div>Offensive Rating = (Points / Possessions) x 100</div>
                        <div>Defensive Rating = (Opponent Points Allowed / Possessions) x 100</div>
                        <div>Possessions = FGA + (0.44 x FTA) - OR + TO</div>
                    </div>
                </template>
            </info-tip>
        </v-card-title>

        <v-skeleton-loader
            v-if="loading"
            type="list-item-two-line, list-item-two-line, list-item-two-line"
        />

        <div v-else>
            <div v-if="ratingRows.length" class="diagram-layout">
                <div ref="chartPane" class="chart-pane">
                    <div class="rating-chart-wrap">
                        <svg class="rating-chart" viewBox="0 0 980 620" role="img" aria-label="Offensive and defensive rating scatter chart">
                            <line :x1="chartBounds.left" :y1="chartBounds.bottom" :x2="chartBounds.right" :y2="chartBounds.bottom" class="axis-line" />
                            <line :x1="chartBounds.left" :y1="chartBounds.top" :x2="chartBounds.left" :y2="chartBounds.bottom" class="axis-line" />

                            <g v-for="tick in yAxisTicks" :key="`y-tick-${tick.value}`">
                                <line
                                    :x1="chartBounds.left"
                                    :y1="tick.y"
                                    :x2="chartBounds.left - 8"
                                    :y2="tick.y"
                                    class="axis-tick"
                                />
                                <text
                                    :x="chartBounds.left - 12"
                                    :y="tick.y + 4"
                                    class="axis-tick-label"
                                    text-anchor="end"
                                >
                                    {{ tick.label }}
                                </text>
                            </g>

                            <g v-for="tick in xAxisTicks" :key="`x-tick-${tick.value}`">
                                <line
                                    :x1="tick.x"
                                    :y1="chartBounds.bottom"
                                    :x2="tick.x"
                                    :y2="chartBounds.bottom + 8"
                                    class="axis-tick"
                                />
                                <text
                                    :x="tick.x"
                                    :y="chartBounds.bottom + 22"
                                    class="axis-tick-label"
                                    text-anchor="middle"
                                >
                                    {{ tick.label }}
                                </text>
                            </g>

                            <line :x1="chartBounds.left" :y1="avgY" :x2="chartBounds.right" :y2="avgY" class="average-line" />
                            <line :x1="avgX" :y1="chartBounds.top" :x2="avgX" :y2="chartBounds.bottom" class="average-line" />

                            <text :x="avgX + 8" :y="chartBounds.top + 18" class="average-label">AVERAGE</text>
                            <text :x="chartBounds.left + 6" :y="avgY + 18" class="average-label">AVERAGE</text>

                            <text :x="chartBounds.left + 8" :y="chartBounds.top + 24" class="quadrant-label">GOOD OFFENSE / BAD DEFENSE</text>
                            <text :x="chartBounds.right - 210" :y="chartBounds.top + 24" class="quadrant-label">GOOD OFFENSE / GOOD DEFENSE</text>
                            <text :x="chartBounds.left + 8" :y="chartBounds.bottom - 14" class="quadrant-label">BAD OFFENSE / BAD DEFENSE</text>
                            <text :x="chartBounds.right - 206" :y="chartBounds.bottom - 14" class="quadrant-label">BAD OFFENSE / GOOD DEFENSE</text>

                            <text :x="chartBounds.left - 2" :y="chartBounds.top - 18" class="axis-label" text-anchor="start">Offensive rating</text>
                            <text :x="chartBounds.right - 8" :y="chartBounds.bottom + 34" class="axis-label" text-anchor="end">Defensive rating</text>

                            <g v-for="point in chartPoints" :key="`point-${point.id}`">
                                <image
                                    v-if="point.logo"
                                    :href="point.logo"
                                    :x="point.x - 23"
                                    :y="point.y - 23"
                                    width="46"
                                    height="46"
                                    preserveAspectRatio="xMidYMid meet"
                                />
                                <text
                                    v-else
                                    :x="point.x"
                                    :y="point.y + 4"
                                    class="fallback-team"
                                    text-anchor="middle"
                                >
                                    {{ point.shortName }}
                                </text>
                                <text
                                    :x="netLabelX(point)"
                                    :y="netLabelY(point)"
                                    :text-anchor="netLabelAnchor(point)"
                                    :class="point.net >= 0 ? 'net-positive' : 'net-negative'"
                                    class="net-label"
                                >
                                    {{ point.net >= 0 ? `+${formatValue(point.net)}` : formatValue(point.net) }}
                                </text>
                            </g>
                        </svg>
                    </div>
                </div>

                <div ref="listPane" class="list-pane" :style="listPaneStyle">
                    <div
                        v-for="team in ratingRows"
                        :key="team.id"
                        class="team-row"
                    >
                        <div class="team-meta">
                            <v-img
                                v-if="team.logo"
                                :src="team.logo"
                                :class="['team-logo', { 'team-logo-clickable': !!team.routeId }]"
                                contain
                                @click.stop="goToTeam(team)"
                            />
                            <div class="team-copy">
                                <div class="team-name secondary-font">{{ team.name }}</div>
                                <div class="team-details secondary-font">
                                    PTS {{ formatValue(team.points) }} | OPP PTS {{ formatValue(team.opponentPointsAllowed) }} | POSS {{ formatValue(team.possessions) }}
                                </div>
                            </div>
                        </div>

                        <div class="rating-visual-group">
                            <div class="rating-visual">
                                <div class="rating-label secondary-font">ORtg</div>
                                <div class="rating-track">
                                    <div class="rating-fill rating-fill-off" :style="{ width: `${team.offWidth}%` }" />
                                </div>
                                <div class="rating-value secondary-font">{{ formatValue(team.offensiveRating) }}</div>
                            </div>

                            <div class="rating-visual">
                                <div class="rating-label secondary-font">DRtg</div>
                                <div class="rating-track">
                                    <div class="rating-fill rating-fill-def" :style="{ width: `${team.defWidth}%` }" />
                                </div>
                                <div class="rating-value secondary-font">{{ formatValue(team.defensiveRating) }}</div>
                            </div>

                            <div class="rating-visual">
                                <div class="rating-label secondary-font">NRtg</div>
                                <div class="rating-track">
                                    <div
                                        :class="[
                                            'rating-fill',
                                            team.netRating >= 0 ? 'rating-fill-net-positive' : 'rating-fill-net-negative'
                                        ]"
                                        :style="{ width: `${team.netWidth}%` }"
                                    />
                                </div>
                                <div class="rating-value secondary-font">{{ team.netRating >= 0 ? `+${formatValue(team.netRating)}` : formatValue(team.netRating) }}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="!ratingRows.length" class="secondary-font empty-state">
                No team stats available for the selected filters.
            </div>
        </div>
    </v-card>
</template>

<script>
    export default {
        name: 'OffensiveRatingDiagram',
        props: {
            teams: {
                type: Array,
                default: () => []
            },
            loading: {
                type: Boolean,
                default: false
            },
            assetsUrl: {
                type: String,
                default: 'https://storage.googleapis.com'
            }
        },
        data() {
            return {
                listPaneMaxHeight: null
            }
        },
        computed: {
            listPaneStyle() {
                if (!this.listPaneMaxHeight) return null
                return {
                    maxHeight: `${this.listPaneMaxHeight}px`,
                    overflowY: 'auto'
                }
            },
            pointsByTeamAndMatch() {
                const byTeam = {}

                this.teams.forEach((team, teamIndex) => {
                    const teamKey = this.getTeamKey(team, teamIndex)
                    byTeam[teamKey] = {}

                    const players = Array.isArray(team?.players) ? team.players : []
                    players.forEach(player => {
                        const stats = Array.isArray(player?.stats) ? player.stats : []
                        stats.forEach(stat => {
                            if (stat?.cup) return
                            const matchId = stat?.matchId
                            if (matchId === null || matchId === undefined) return
                            byTeam[teamKey][matchId] = (byTeam[teamKey][matchId] || 0) + (Number(stat?.points) || 0)
                        })
                    })
                })

                return byTeam
            },
            pointsByMatch() {
                const byMatch = {}

                Object.entries(this.pointsByTeamAndMatch).forEach(([teamKey, matchPoints]) => {
                    Object.entries(matchPoints).forEach(([matchId, points]) => {
                        if (!byMatch[matchId]) byMatch[matchId] = {}
                        byMatch[matchId][teamKey] = points
                    })
                })

                return byMatch
            },
            ratingRows() {
                const rows = this.teams.map((team, teamIndex) => {
                    const teamKey = this.getTeamKey(team, teamIndex)
                    const totals = this.getTotals(team)
                    const fga = totals.twoPointAttempts + totals.threePointAttempts
                    const possessions = fga + (0.44 * totals.freeThrowAttempts) - totals.offensiveRebounds + totals.turnovers
                    const safePossessions = possessions > 0 ? possessions : 0
                    const offensiveRating = safePossessions > 0
                        ? (totals.points / safePossessions) * 100
                        : 0
                    const opponentPointsAllowed = this.getOpponentPointsAllowed(teamKey)
                    const defensiveRating = safePossessions > 0
                        ? (opponentPointsAllowed / safePossessions) * 100
                        : 0
                    const netRating = offensiveRating - defensiveRating

                    return {
                        id: team.id || team._id || team.name,
                        routeId: team.id || team._id || null,
                        teamKey,
                        name: team.name || 'Team',
                        logo: this.getLogo(team),
                        points: totals.points,
                        opponentPointsAllowed,
                        possessions: safePossessions,
                        offensiveRating,
                        defensiveRating,
                        netRating
                    }
                })

                const sorted = rows.sort((a, b) => b.offensiveRating - a.offensiveRating)
                const maxOffValue = Math.max(...sorted.map(row => row.offensiveRating), 1)
                const maxDefValue = Math.max(...sorted.map(row => row.defensiveRating), 1)
                const maxAbsNetValue = Math.max(...sorted.map(row => Math.abs(row.netRating)), 1)

                return sorted.map(row => ({
                    ...row,
                    offWidth: Math.max(8, (row.offensiveRating / maxOffValue) * 100),
                    defWidth: Math.max(8, (row.defensiveRating / maxDefValue) * 100),
                    netWidth: Math.max(8, (Math.abs(row.netRating) / maxAbsNetValue) * 100)
                }))
            },
            chartBounds() {
                return {
                    left: 58,
                    right: 900,
                    top: 36,
                    bottom: 560
                }
            },
            avgOffensive() {
                if (!this.ratingRows.length) return 0
                const total = this.ratingRows.reduce((sum, row) => sum + row.offensiveRating, 0)
                return total / this.ratingRows.length
            },
            avgDefensive() {
                if (!this.ratingRows.length) return 0
                const total = this.ratingRows.reduce((sum, row) => sum + row.defensiveRating, 0)
                return total / this.ratingRows.length
            },
            offMin() {
                return this.offRawMin - this.offPadding
            },
            offMax() {
                return this.offRawMax + this.offPadding
            },
            defMin() {
                return this.defRawMin - this.defPadding
            },
            defMax() {
                return this.defRawMax + this.defPadding
            },
            offRawMin() {
                if (!this.ratingRows.length) return 100
                return Math.min(...this.ratingRows.map(row => row.offensiveRating))
            },
            offRawMax() {
                if (!this.ratingRows.length) return 120
                return Math.max(...this.ratingRows.map(row => row.offensiveRating))
            },
            defRawMin() {
                if (!this.ratingRows.length) return 100
                return Math.min(...this.ratingRows.map(row => row.defensiveRating))
            },
            defRawMax() {
                if (!this.ratingRows.length) return 120
                return Math.max(...this.ratingRows.map(row => row.defensiveRating))
            },
            offPadding() {
                return Math.max(2, (this.offRawMax - this.offRawMin) * 0.2)
            },
            defPadding() {
                return Math.max(2, (this.defRawMax - this.defRawMin) * 0.2)
            },
            avgX() {
                return this.defensiveToX(this.avgDefensive)
            },
            avgY() {
                return this.offensiveToY(this.avgOffensive)
            },
            plotSafeBounds() {
                return {
                    left: this.chartBounds.left + 24,
                    right: this.chartBounds.right - 24,
                    top: this.chartBounds.top + 64,
                    bottom: this.chartBounds.bottom - 52
                }
            },
            chartPoints() {
                return this.ratingRows.map(row => ({
                    ...row,
                    x: this.clamp(this.defensiveToX(row.defensiveRating), this.plotSafeBounds.left, this.plotSafeBounds.right),
                    y: this.clamp(this.offensiveToY(row.offensiveRating), this.plotSafeBounds.top, this.plotSafeBounds.bottom),
                    net: row.offensiveRating - row.defensiveRating,
                    shortName: String(row.name || 'T').slice(0, 2).toUpperCase()
                }))
            },
            yAxisTicks() {
                const steps = 4
                const span = this.offMax - this.offMin
                return Array.from({ length: steps + 1 }, (_, index) => {
                    const ratio = index / steps
                    const value = this.offMin + (span * ratio)
                    return {
                        value,
                        y: this.offensiveToY(value),
                        label: Number(value).toFixed(1)
                    }
                })
            },
            xAxisTicks() {
                const steps = 4
                const span = this.defMax - this.defMin
                return Array.from({ length: steps + 1 }, (_, index) => {
                    const ratio = index / steps
                    const value = this.defMin + (span * ratio)
                    return {
                        value,
                        x: this.defensiveToX(value),
                        label: Number(value).toFixed(1)
                    }
                })
            }
        },
        watch: {
            loading() {
                this.$nextTick(this.updateListPaneMaxHeight)
            },
            ratingRows() {
                this.$nextTick(this.updateListPaneMaxHeight)
            }
        },
        mounted() {
            window.addEventListener('resize', this.updateListPaneMaxHeight)
            this.$nextTick(this.updateListPaneMaxHeight)
        },
        beforeDestroy() {
            window.removeEventListener('resize', this.updateListPaneMaxHeight)
        },
        methods: {
            updateListPaneMaxHeight() {
                if (!this.$refs.chartPane || (this.$vuetify && this.$vuetify.breakpoint && this.$vuetify.breakpoint.width <= 960)) {
                    if (this.listPaneMaxHeight !== null) {
                        this.listPaneMaxHeight = null
                    }
                    return
                }

                const chartHeight = this.$refs.chartPane.clientHeight || 0
                if (chartHeight <= 0) return

                if (this.listPaneMaxHeight === null || Math.abs(this.listPaneMaxHeight - chartHeight) > 1) {
                    this.listPaneMaxHeight = chartHeight
                }
            },
            offensiveToY(value) {
                const span = Math.max(0.1, this.offMax - this.offMin)
                const normalized = (value - this.offMin) / span
                return this.chartBounds.bottom - (normalized * (this.chartBounds.bottom - this.chartBounds.top))
            },
            defensiveToX(value) {
                const span = Math.max(0.1, this.defMax - this.defMin)
                const normalized = (value - this.defMin) / span
                // Defensive rating axis is reversed: better defense to the right.
                return this.chartBounds.right - (normalized * (this.chartBounds.right - this.chartBounds.left))
            },
            getTeamKey(team = {}, teamIndex = 0) {
                return String(team?.id ?? team?._id ?? team?.name ?? `team-${teamIndex}`)
            },
            getLogo(team = {}) {
                const raw = team.logo || team.image || null
                if (!raw) return null
                if (/^https?:\/\//i.test(raw)) return raw
                return `${this.assetsUrl}/${raw}`
            },
            getOpponentPointsAllowed(teamKey) {
                const matchPoints = this.pointsByTeamAndMatch[teamKey] || {}
                let opponentPointsAllowed = 0

                Object.entries(matchPoints).forEach(([matchId, teamPoints]) => {
                    const participants = this.pointsByMatch[matchId] || {}
                    const totalInMatch = Object.values(participants).reduce((sum, value) => sum + (Number(value) || 0), 0)
                    opponentPointsAllowed += Math.max(0, totalInMatch - (Number(teamPoints) || 0))
                })

                return opponentPointsAllowed
            },
            getTotals(team = {}) {
                if (team.cum) {
                    return {
                        points: Number(team.cum.points) || 0,
                        twoPointAttempts: Number(team.cum.twoPointAttempts) || 0,
                        threePointAttempts: Number(team.cum.threePointAttempts) || 0,
                        freeThrowAttempts: Number(team.cum.freeThrowAttempts) || 0,
                        offensiveRebounds: Number(team.cum.offensiveRebounds) || 0,
                        turnovers: Number(team.cum.turnovers) || 0
                    }
                }

                const totals = {
                    points: 0,
                    twoPointAttempts: 0,
                    threePointAttempts: 0,
                    freeThrowAttempts: 0,
                    offensiveRebounds: 0,
                    turnovers: 0
                }

                const stats = Array.isArray(team.stats) ? team.stats : []
                stats.forEach(stat => {
                    totals.points += Number(stat.points) || 0
                    totals.twoPointAttempts += Number(stat.twoPointAttempts) || 0
                    totals.threePointAttempts += Number(stat.threePointAttempts) || 0
                    totals.freeThrowAttempts += Number(stat.freeThrowAttempts) || 0
                    totals.offensiveRebounds += Number(stat.offensiveRebounds) || 0
                    totals.turnovers += Number(stat.turnovers) || 0
                })

                return totals
            },
            formatValue(value) {
                return Number(value || 0).toFixed(1)
            },
            clamp(value, min, max) {
                return Math.min(max, Math.max(min, value))
            },
            netLabelAnchor(point) {
                return point.x > (this.chartBounds.right - 120) ? 'end' : 'start'
            },
            netLabelX(point) {
                return this.netLabelAnchor(point) === 'end' ? point.x - 28 : point.x + 28
            },
            netLabelY(point) {
                if (point.y < this.chartBounds.top + 72) {
                    return point.y + 34
                }
                if (point.y > this.chartBounds.bottom - 68) {
                    return point.y - 28
                }
                return point.y - 10
            },
            goToTeam(team) {
                const teamId = team && team.routeId
                if (!teamId) return

                this.$router.push({ name: 'Team', params: { id: teamId } })
            }
        }
    }
</script>

<style scoped>
.section-title {
    border-bottom: 1px solid #d7dbe3;
    margin-bottom: 8px;
    width: 100%;
}

.title-row {
    display: flex;
    align-items: center;
}

.info-icon {
    cursor: pointer;
}

.tooltip-content {
    font-size: 13px;
    line-height: 1.4;
    width: 100%;
}

::v-deep .v-tooltip__content.diagram-tooltip-surface {
    white-space: normal !important;
    overflow-wrap: break-word;
    word-break: normal;
}

::v-deep .v-tooltip__content.diagram-tooltip-surface--off {
    width: min(520px, calc(100vw - 32px)) !important;
    max-width: min(520px, calc(100vw - 32px)) !important;
}

.diagram-layout {
    display: flex;
    gap: 14px;
    align-items: stretch;
}

.chart-pane,
.list-pane {
    flex: 1;
    min-width: 0;
}

.chart-pane {
    display: flex;
    align-items: center;
}

.rating-chart-wrap {
    margin: 0;
    border: 0;
    border-radius: 0;
    background: transparent;
    padding: 0;
    width: 100%;
}

.rating-chart {
    width: 100%;
    height: auto;
    display: block;
    aspect-ratio: 980 / 620;
}

.axis-line {
    stroke: #2f3f53;
    stroke-width: 2.4;
}

.axis-tick {
    stroke: #4c5b6f;
    stroke-width: 1.4;
}

.axis-tick-label {
    fill: #55657a;
    font-size: 11px;
    font-weight: 700;
}

.average-line {
    stroke: #7a8899;
    stroke-width: 1.5;
}

.axis-label {
    fill: #2f3f53;
    font-size: 14px;
    font-weight: 700;
}

.average-label {
    fill: #616e7f;
    font-size: 12px;
    font-weight: 700;
}

.quadrant-label {
    fill: #5e6c7d;
    font-size: 11px;
    font-weight: 700;
}

.logo-ring {
    fill: #fff;
    stroke: #d2d8e2;
    stroke-width: 1.5;
}

.fallback-team {
    fill: #1b273b;
    font-size: 12px;
    font-weight: 800;
}

.net-label {
    font-size: 15px;
    font-weight: 900;
}

.net-positive {
    fill: #1f9d47;
}

.net-negative {
    fill: #da3e3e;
}

.team-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 10px 0;
    border-bottom: 1px solid #eceff5;
}

.team-row:last-child {
    border-bottom: 0;
}

.list-pane {
    max-height: none;
    overflow: auto;
    padding-right: 4px;
}

.team-meta {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 200px;
}

.team-logo {
    width: 52px;
    height: 52px;
    border-radius: 0;
    background: #fff;
}

.team-logo-clickable {
    cursor: pointer;
}

.team-copy {
    text-align: left;
}

.team-name {
    font-size: 16px;
    font-weight: 800;
    color: #182a3d;
}

.team-details {
    font-size: 12px;
    font-weight: 600;
    color: #5d6f83;
}

.rating-visual {
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 1;
}

.rating-visual-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1;
    max-width: 480px;
}

.rating-label {
    min-width: 38px;
    text-align: right;
    font-size: 12px;
    font-weight: 800;
    color: #4b5a6f;
}

.rating-track {
    width: 100%;
    height: 12px;
    border-radius: 999px;
    background: #eceff4;
    overflow: hidden;
}

.rating-fill {
    height: 100%;
    border-radius: 999px;
}

.rating-fill-off {
    background: linear-gradient(90deg, #ef4444 0%, #ff7133 70%, orange 100%);
}

.rating-fill-def {
    background: linear-gradient(90deg, #5b2b7f 0%, #3a2d7a 60%, #233b74 100%);
}

.rating-fill-net-positive {
    background: linear-gradient(90deg, #1f9d47 0%, #2fb157 70%, #6bcf7f 100%);
}

.rating-fill-net-negative {
    background: linear-gradient(90deg, #b42323 0%, #d33c3c 70%, #f07171 100%);
}

.rating-value {
    min-width: 54px;
    text-align: right;
    font-size: 17px;
    font-weight: 900;
    color: #111;
}

.empty-state {
    color: #5d6f83;
    padding: 8px 0;
}

@media only screen and (max-width: 960px) {
    .diagram-layout {
        flex-direction: column;
    }

    .list-pane {
        max-height: none;
        overflow: visible;
        padding-right: 0;
    }

    .team-row {
        flex-direction: column;
        align-items: flex-start;
    }

    .team-meta {
        min-width: 0;
    }

    .rating-visual {
        width: 100%;
        max-width: none;
    }

    .rating-visual-group {
        width: 100%;
        max-width: none;
    }
}
</style>
